import Check from '@mui/icons-material/Check'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useGetCustomVariables } from '@/service-library/hooks/custom-variables'
import useGetEventsForWorkflowState from '@/services/hooks/useGetEventsForWorkflowState'
import { WorkflowStateAction } from '@/types/workflow-state-actions'

type AITriggerEventDisplayProps = {
  action: WorkflowStateAction
}

type AITriggerEventOption = {
  conditions: string
  event_id?: string
  instructions_variable_id?: string
}

type AITriggerEventParams = {
  options: AITriggerEventOption[]
  include_reasoning: boolean
  default_option?: string
}

export default function AITriggerEventDisplay({
  action,
}: AITriggerEventDisplayProps) {
  const params = action.params as AITriggerEventParams

  const { customEvents } = useGetEventsForWorkflowState()

  const hasInstructionsVariable = params.options.some(
    (option) => option.instructions_variable_id,
  )

  const { customVariables } = useGetCustomVariables({
    filters: {
      limit: '1000',
      fields__only: 'id,name',
      id__in: params.options
        .map((option) => option.instructions_variable_id)
        .filter(Boolean)
        .join(','),
    },
    enabled: hasInstructionsVariable,
  })

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {action.action_type?.description}
      </Typography>

      <Stack spacing={1} sx={{ my: 2 }}>
        {params.options.map((option, index) => {
          const event_id = option.event_id
          const eventName = customEvents.find(
            (event) => event.id === event_id,
          )?.name
          const variableName =
            customVariables.find(
              (variable) => variable.id === option.instructions_variable_id,
            )?.name || '(Variable not found)'
          return (
            <div key={index}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ lineHeight: 1 }}
              >
                Option {index + 1}
              </Typography>
              <Typography variant="body2">
                <b>Event:</b>{' '}
                <Box
                  component="span"
                  sx={{
                    color: event_id ? 'success.main' : 'text.secondary',
                    fontStyle: event_id ? 'normal' : 'italic',
                  }}
                >
                  {event_id ? eventName : 'None'}
                </Box>
              </Typography>
              <Typography variant="body2">
                <b>Conditions:</b>{' '}
                <Box
                  component="span"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {option.conditions}
                </Box>
              </Typography>
              {option.instructions_variable_id && (
                <Tooltip title={variableName}>
                  <Typography variant="body2" noWrap>
                    <b>Instructions Variable:</b>{' '}
                    <Box
                      component="span"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {variableName}
                    </Box>
                  </Typography>
                </Tooltip>
              )}
            </div>
          )
        })}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        If PixyAI is unable to make a clear decision,{' '}
        {params.default_option ? (
          <>
            it will pick{' '}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ lineHeight: 1 }}
            >
              Option {params.default_option}
            </Typography>
            .
          </>
        ) : (
          'the document will be flagged for review.'
        )}
      </Typography>
      {params.include_reasoning && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Check fontSize="small" color="success" />
          <Typography variant="body2">Include Reasoning Log</Typography>
        </Stack>
      )}
    </Stack>
  )
}
